import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from './api.service';

/**
 * ExternalEoiService
 *
 * Wraps the External EOI endpoints:
 *  - Config + link management calls are authenticated (agent side).
 *  - Public form calls (`getPublicForm` / `submitPublicForm`) are hit by the
 *    unauthenticated buyer-facing page. Their `/external-eoi/public/...`
 *    prefix is whitelisted in ApiService.publicApiPathPrefixes so they run
 *    without a JWT.
 */
@Injectable({
  providedIn: 'root',
})
export class ExternalEoiService {
  constructor(private api: ApiService) {}

  // ---- Configuration (per project) ----------------------------------------

  /** Load the saved external-EOI form configuration for a project. */
  getConfig = (projectId: string): Observable<any> => {
    return this.api.get(`/external-eoi/config/${projectId}?office_id=${this.api.getOfficeId()}`);
  };

  /** Create/replace the external-EOI form configuration for a project. */
  saveConfig = (projectId: string, payload: any): Observable<any> => {
    return this.api.put(`/external-eoi/config/${projectId}`, {
      ...payload,
      office_id: this.api.getOfficeId(),
      user_id: this.api.getUserId(),
    });
  };

  /**
   * The office's own SMTP accounts (`user_smtps`), offered as the sender of the
   * buyer's access passcode.
   *
   * An office can hold several and none is flagged as the default, so the
   * backend will not guess — one has to be picked per project via
   * `otp_smtp_id` on the config, or the passcode falls back to the default
   * mailer and never reaches a real inbox. No passwords are returned.
   */
  getSmtpAccounts = (): Observable<any> => {
    return this.api.get(`/external-eoi/smtp-accounts?office_id=${this.api.getOfficeId()}`);
  };

  // ---- Link management -----------------------------------------------------

  /**
   * List generated links for a project.
   *
   * `filter` narrows the list to one originating record. The endpoint is
   * project-scoped, so a backend that does not yet honour these params simply
   * returns everything — callers showing links on a single record filter the
   * result themselves.
   */
  getLinks = (projectId: string, filter?: { eoiId?: string | null; leadId?: string | null }): Observable<any> => {
    const scope =
      (filter?.eoiId ? `&eoi_id=${encodeURIComponent(filter.eoiId)}` : '') + (filter?.leadId ? `&lead_id=${encodeURIComponent(filter.leadId)}` : '');
    return this.api.get(`/external-eoi/links?project_id=${projectId}&office_id=${this.api.getOfficeId()}${scope}`);
  };

  /**
   * Generate a new external EOI link.
   * @param payload { project_id, lot_id?, contact_id?, lead_id?, expires_at? }
   */
  generateLink = (payload: any): Observable<any> => {
    return this.api.post('/external-eoi/links', {
      ...payload,
      office_id: this.api.getOfficeId(),
      user_id: this.api.getUserId(),
    });
  };

  /**
   * Edit an existing link's details (buyer, lot, expiry, deposit) without
   * reissuing it — the URL already in the buyer's inbox keeps working.
   */
  updateLink = (linkId: string, payload: any): Observable<any> => {
    return this.api.patch(`/external-eoi/links/${linkId}`, {
      ...payload,
      office_id: this.api.getOfficeId(),
      user_id: this.api.getUserId(),
    });
  };

  /**
   * Saves answers an agent has typed into a link's form themselves ("View Link" → "Save for
   * buyer") so they're already filled in when the buyer opens the real link. Same per-link PATCH
   * as `updateLink`, carrying a `draft_answers` map keyed by field key — see
   * external-eoi-client-review-2026-09-07-backend-requirements.md §1 for the contract.
   */
  saveLinkDraftAnswers = (linkId: string, draftAnswers: Record<string, any>): Observable<any> => {
    return this.updateLink(linkId, { draft_answers: draftAnswers });
  };

  /** Deactivate (cancel) a link so it can no longer be opened. */
  deactivateLink = (linkId: string): Observable<any> => {
    return this.api.patch(`/external-eoi/links/${linkId}/deactivate`, {
      user_id: this.api.getUserId(),
    });
  };

  /**
   * Reopen a submitted link so the buyer can edit and resubmit through the same URL — the token
   * and any answers already on file are kept; only the "already completed" lock is lifted. Distinct
   * from `regenerateLink`, which invalidates the old URL and mints a new one.
   */
  unlockLink = (linkId: string): Observable<any> => {
    return this.api.patch(`/external-eoi/links/${linkId}/unlock`, {
      user_id: this.api.getUserId(),
    });
  };

  /** Regenerate a fresh token for an existing link (invalidates the old one). */
  regenerateLink = (linkId: string): Observable<any> => {
    return this.api.patch(`/external-eoi/links/${linkId}/regenerate`, {
      user_id: this.api.getUserId(),
    });
  };

  /**
   * Permanently remove a link so it no longer has to be kept around in the
   * list. Callable regardless of status — the UI confirms before calling
   * this for a link that is still active.
   */
  deleteLink = (linkId: string): Observable<any> => {
    return this.api.delete(`/external-eoi/links/${linkId}`);
  };

  /**
   * Authenticated view of a link's live form — the same payload as the public
   * `GET /external-eoi/public/form/:token` once verified, but gated by the
   * agent's own login instead of the buyer's emailed passcode.
   *
   * Used for "View Link" so an agent isn't sent to the public URL and stuck
   * behind the OTP gate (the code goes to the buyer's inbox, not theirs). The
   * view is audit-logged server-side. A 403 means the agent is out of scope
   * for this link.
   */
  getLinkView = (linkId: string): Observable<any> => {
    return this.api.get(`/external-eoi/links/${linkId}/view`);
  };

  // ---- Lookups used by the generate/send modal -----------------------------

  /** Lots for a project, filtered to sellable status server-side if needed. */
  getProjectLots = (projectId: string): Observable<any> => {
    return this.api.post(`/unit-short-detail?office_id=${this.api.getOfficeId()}`, { project_id: [projectId] });
  };

  /**
   * Projects for the office. Needed where the caller has no project in hand —
   * a contact, for instance, can be associated with several.
   */
  getProjects = (): Observable<any> => {
    return this.api.get(`/projects-name-list?office_id=${this.api.getOfficeId()}`);
  };

  // ---- Public (unauthenticated buyer form) --------------------------------

  /**
   * Resolve the readable `/{project}/eoi/{lot}/{client}` path to a link token.
   *
   * The slugs are minted by the backend when the link is created, so this is a
   * lookup rather than a reversible encoding — two buyers of the same name on
   * one project are distinguished by the `-2`, `-3`… suffix on the client
   * slug. Returns `{ data: { token } }`; everything downstream then runs on
   * the token exactly as the original URL does.
   */
  resolvePublicSlug = (projectSlug: string, lotSlug: string, clientSlug: string): Observable<any> => {
    const path = [projectSlug, lotSlug, clientSlug].map(encodeURIComponent).join('/');
    return this.api.get(`/external-eoi/public/resolve/${path}`);
  };

  /**
   * Send a one-time passcode to the buyer's email for this link.
   *
   * The form holds personal information and can be reopened, so access is
   * gated by an emailed OTP rather than the link alone.
   */
  requestPublicOtp = (token: string): Observable<any> => {
    return this.api.post(`/external-eoi/public/otp/${token}`, {});
  };

  /** Exchange an emailed passcode for a short-lived access grant. */
  verifyPublicOtp = (token: string, code: string): Observable<any> => {
    return this.api.post(`/external-eoi/public/otp/${token}/verify`, { code });
  };

  /**
   * Fetch the public form definition + prefilled values for a token.
   * `accessToken` is the grant returned by verifyPublicOtp.
   */
  getPublicForm = (token: string, accessToken?: string | null): Observable<any> => {
    const suffix = accessToken ? `?access=${encodeURIComponent(accessToken)}` : '';
    return this.api.get(`/external-eoi/public/form/${token}${suffix}`);
  };

  /** Submit the completed buyer form (creates/updates the EOI on the backend). */
  submitPublicForm = (token: string, payload: any, accessToken?: string | null): Observable<any> => {
    return this.api.post(`/external-eoi/public/submit/${token}`, {
      ...payload,
      access: accessToken || undefined,
    });
  };
}
